/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Toggle } from 'carbon-addons-iot-react';
import { useField } from 'formik';

import { TextValueFormik } from 'Common';

const propTypes = {
  id: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  'aria-label': PropTypes.string.isRequired,
  labelA: PropTypes.string,
  labelB: PropTypes.string,

  /**
   * When true, if form input is disabled it will render as if it were
   * just a normal (potentially labelled) text value and not as a disabled input.
   * This pattern (input when enabled, plaintext when disabled) is used repeatedly
   * throughout the connect design.
   */
  renderAsTextWhenDisabled: PropTypes.bool,
};

const defaultProps = {
  disabled: false,
  labelA: '',
  labelB: '',
  renderAsTextWhenDisabled: true,
};

const ToggleSmallFormik = ({
  id,
  disabled,
  renderAsTextWhenDisabled,
  'aria-label': ariaLabel,
  labelA,
  labelB,
}) => {
  const [field, meta] = useField(id);

  return renderAsTextWhenDisabled && disabled ? (
    <TextValueFormik id={id} hideLabel />
  ) : (
    <Toggle
      aria-label={ariaLabel}
      size="sm"
      id={id}
      name={id}
      onToggle={isToggled => {
        field.onChange({
          type: 'change',
          target: { value: isToggled, name: id },
        });
      }}
      defaultToggled={field.value}
      toggled={field.value}
      onBlur={field.onBlur}
      invalid={meta.error && meta.touched}
      disabled={disabled}
      labelA={labelA}
      labelB={labelB}
    />
  );
};

ToggleSmallFormik.propTypes = propTypes;
ToggleSmallFormik.defaultProps = defaultProps;

export default ToggleSmallFormik;
